/**
 * Paginated Spaces directory used by Search (Spaces tab).
 * Query comes from the parent Search input — no local search field.
 */

import { createApiClient, type PublicSpace } from '@adieuu/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { SpaceCard } from '../../components/SpaceCard';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { useAppConfig } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { useIdentity } from '../../hooks/useIdentity';
import '../../styles/_spaces.scss';

const PAGE_SIZE = 30;

export interface SearchSpacesDirectoryProps {
  /** Normalized directory query; empty browses the full public catalog. */
  query: string;
  /** Show Create Space when the viewer is allowed to create. Default true. */
  showCreateCta?: boolean;
}

export function SearchSpacesDirectory({
  query,
  showCreateCta = true,
}: SearchSpacesDirectoryProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { apiBaseUrl } = useAppConfig();
  const { session } = useAuth();
  const { status: identityStatus } = useIdentity();
  const isLoggedIn = identityStatus === 'logged_in';
  const isPlatformAdmin = session?.isPlatformAdmin === true;
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);

  const [spaces, setSpaces] = useState<PublicSpace[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [canCreateSpace, setCanCreateSpace] = useState(false);
  const fetchSeq = useRef(0);

  const normalizedQuery = query.trim();

  const fetchSpaces = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(false);
    try {
      const res = await api.spaces.discover({
        limit: PAGE_SIZE,
        ...(normalizedQuery ? { q: normalizedQuery } : {}),
      });
      if (seq !== fetchSeq.current) return;
      if (res.success && res.data) {
        setSpaces(res.data.spaces);
        setCursor(res.data.cursor);
      } else {
        setError(true);
      }
    } catch {
      if (seq === fetchSeq.current) setError(true);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [api, normalizedQuery]);

  useEffect(() => {
    void fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (!showCreateCta || !isLoggedIn) {
      setCanCreateSpace(false);
      return;
    }
    if (isPlatformAdmin) {
      setCanCreateSpace(true);
      return;
    }
    let cancelled = false;
    void api.spaces.getCreationEnabled().then((res) => {
      if (cancelled) return;
      setCanCreateSpace(res.success && res.data?.enabled === true);
    });
    return () => {
      cancelled = true;
    };
  }, [showCreateCta, isLoggedIn, isPlatformAdmin, api]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !cursor) return;
    const seq = fetchSeq.current;
    setLoadingMore(true);
    try {
      const res = await api.spaces.discover({
        limit: PAGE_SIZE,
        cursor,
        ...(normalizedQuery ? { q: normalizedQuery } : {}),
      });
      if (seq !== fetchSeq.current) return;
      if (res.success && res.data) {
        const { spaces: nextSpaces, cursor: nextCursor } = res.data;
        setSpaces((prev) => [...prev, ...nextSpaces]);
        setCursor(nextCursor);
      } else {
        toast.error(t('spaces.loadMoreError'));
      }
    } catch {
      if (seq === fetchSeq.current) toast.error(t('spaces.loadMoreError'));
    } finally {
      setLoadingMore(false);
    }
  }, [api, cursor, normalizedQuery, loadingMore, toast, t]);

  return (
    <div className="search-spaces-directory">
      {showCreateCta && canCreateSpace ? (
        <div className="spaces-directory-controls search-spaces-directory-controls">
          <Link to="/spaces/new" className="btn btn-primary btn-md spaces-create-cta">
            {t('spaces.create.cta')}
          </Link>
        </div>
      ) : null}

      {loading ? (
        <div className="spaces-loading">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Card variant="elevated" className="spaces-state">
          <p className="spaces-state-heading">{t('spaces.error.heading')}</p>
          <p className="spaces-state-body">{t('spaces.error.body')}</p>
          <Button variant="secondary" onClick={() => void fetchSpaces()}>
            {t('spaces.error.retry')}
          </Button>
        </Card>
      ) : spaces.length === 0 ? (
        <Card variant="elevated" className="spaces-state">
          <p className="spaces-state-heading">
            {normalizedQuery ? t('search.noResults') : t('spaces.empty.heading')}
          </p>
          <p className="spaces-state-body">
            {normalizedQuery ? t('search.noResultsHint') : t('spaces.empty.body')}
          </p>
        </Card>
      ) : (
        <>
          <div className="spaces-grid">
            {spaces.map((space) => (
              <SpaceCard key={space.id} space={space} />
            ))}
          </div>

          {cursor ? (
            <div className="spaces-load-more">
              <Button
                variant="secondary"
                onClick={() => void handleLoadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? <Spinner size="sm" /> : t('spaces.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
